import { postRequestEditLastInquiryUser } from "@/api/user/home";
import { CreditDataMain } from "@/types/api/user/home.type.data";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import SpinnerKit from "@/shared/components/kit/spinner";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

// Types
type ImageField =
    | 'nid_front_image'
    | 'nid_back_image'
    | 'birth_certificate_image'
    | 'checkbook_image'
    | 'job_image'
    | 'lease_image';

type ImageStatusKey =
    | 'nid_front_image_status'
    | 'nid_back_image_status'
    | 'birth_certificate_status'
    | 'checkbook_image_status'
    | 'job_image_status'
    | 'status_lease_image';

interface ImageConfig {
  field: ImageField;
  statusKey: ImageStatusKey;
  title: string;
  descriptionKey: keyof CreditDataMain;
  srcGetter: (data: CreditDataMain) => string | undefined;
  condition?: (data: CreditDataMain) => boolean;
}

interface UploadState {
  nid_front_image: string;
  nid_back_image: string;
  birth_certificate_image: string;
  checkbook_image: string;
  job_image: string;
  lease_image: string;
}

// Constants
const IMAGE_CONFIGS: ImageConfig[] = [
  {
    field: 'nid_front_image',
    statusKey: 'nid_front_image_status',
    title: 'روی کارت ملی',
    descriptionKey: 'description_front_back_image',
    srcGetter: (data) => data.nid_front_image,
  },
  {
    field: 'nid_back_image',
    statusKey: 'nid_back_image_status',
    title: 'پشت کارت ملی',
    descriptionKey: 'description_nid_back_image',
    srcGetter: (data) => data.nid_back_image,
  },
  {
    field: 'birth_certificate_image',
    statusKey: 'birth_certificate_status',
    title: 'صفحه اول شناسنامه',
    descriptionKey: 'description_birth_certificate_image',
    srcGetter: (data) => data.birth_certificate_image,
  },
  {
    field: 'checkbook_image',
    statusKey: 'checkbook_image_status',
    title: 'تصویر چک ضمانت',
    descriptionKey: 'description_checkbook_image',
    srcGetter: (data) => data.checkbook_image,
  },
  {
    field: 'job_image',
    statusKey: 'job_image_status',
    title: 'فیش حقوقی',
    descriptionKey: 'description_job_image',
    srcGetter: (data) => data.job_image,
  },
  {
    field: 'lease_image',
    statusKey: 'status_lease_image',
    title: 'تصویر جواز کسب',
    descriptionKey: 'description_lease_image',
    srcGetter: (data) => data.lease_image,
    condition: (data) => !!data.lease_image,
  },
];

// Sub-components
const ImageUploadBox: React.FC<{
  src: string | undefined;
  title: string;
  description: string;
  uploadKey: ImageField;
  onImageChange: (key: ImageField, file: File) => void;
  showOverlay?: boolean;
}> = ({ src, title, description, uploadKey, onImageChange, showOverlay = true }) => {
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageChange(uploadKey, file);
    }
  }, [uploadKey, onImageChange]);

  return (
      <label style={{ cursor: "pointer" }} onClick={(e) => e.stopPropagation()}>
        <StackKit
            sx={{
              width: "183px",
              minHeight: "183px",
              paddingTop: "5px",
              background: "linear-gradient(97.77deg, rgba(0, 0, 0, 0.4) -9.73%, rgba(23, 23, 23, 0.281106) 16.04%, rgba(115, 103, 240, 0.25) 50.23%, rgba(65, 65, 65, 0.231942) 87.66%, rgba(0, 0, 0, 0.16) 109.42%)",
              borderRadius: "8px",
              position: "relative",
            }}
            justifyContent="center"
            alignItems="center"
        >
          <BoxKit sx={{ width: "152px", height: "100px", overflow: "hidden", borderRadius: "8px" }}>
            <img src={src} alt={title} style={{ width: "100%", height: "100%" }} />
          </BoxKit>

          {showOverlay && (
              <div style={{
                position: "absolute",
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0,0,0,0.4)",
                borderRadius: "8px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "white",
                paddingTop: "4px",
              }}>
                <span>انتخاب عکس</span>
              </div>
          )}

          <TypoGraphyKit variant="body1" sx={{ color: "red", fontSize: "10px" }}>
            *** {description}
          </TypoGraphyKit>
          <TypoGraphyKit variant="body1">{title}</TypoGraphyKit>

          <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
          />
        </StackKit>
      </label>
  );
};

const LoadingOverlay: React.FC = () => (
    <StackKit
        justifyContent="center"
        alignItems="center"
        sx={{
          position: "absolute",
          top: "-10px",
          right: 0,
          left: 0,
          bottom: "-10px",
          bgcolor: "#00000052",
          zIndex: 1000,
        }}
    >
      <SpinnerKit color="#fff" />
    </StackKit>
);

// Main Component
const Correction: React.FC<{ userInquiry?: CreditDataMain }> = ({ userInquiry }) => {
  const navigate = useNavigate();

  const [uploadState, setUploadState] = React.useState<UploadState>({
    nid_front_image: "",
    nid_back_image: "",
    birth_certificate_image: "",
    checkbook_image: "",
    job_image: "",
    lease_image: "",
  });

  const { mutate: submitEdit, isPending: isSubmitting } = useMutation({
    mutationFn: postRequestEditLastInquiryUser,
    onSuccess: () => {
      toast.success("ویرایش با موفقیت انجام شد");
      setTimeout(() => window.location.reload(), 200);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در ویرایش اطلاعات");
    },
  });

  const handleImageChange = useCallback((key: ImageField, file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadState((prev) => ({
        ...prev,
        [key]: reader.result as string,
      }));
    };
    reader.readAsDataURL(file);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!userInquiry) return;

    const updatedData = {
      ...userInquiry,
      ...Object.fromEntries(
          Object.entries(uploadState).filter(([_, value]) => !!value)
      ),
    };

    submitEdit(updatedData);
  }, [userInquiry, uploadState, submitEdit]);

  // Check if any image is rejected
  const hasRejectedImages = useMemo(() => {
    if (!userInquiry) return false;
    return IMAGE_CONFIGS.some((config) => {
      const status = userInquiry[config.statusKey];
      return status !== 1 && (config.condition ? config.condition(userInquiry) : true);
    });
  }, [userInquiry]);

  if (!userInquiry || !hasRejectedImages) {
    return null;
  }

  const getImageSrc = (config: ImageConfig) => {
    const uploaded = uploadState[config.field];
    return uploaded || config.srcGetter(userInquiry);
  };

  const shouldShowImage = (config: ImageConfig) => {
    const status = userInquiry[config.statusKey];
    return status !== 1 && (config.condition ? config.condition(userInquiry) : true);
  };

  return (
      <Grid size={12} sx={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "column",
        minHeight: "486px",
        position: "relative",
      }} gap={2}>
        {isSubmitting && <LoadingOverlay />}

        <StackKit alignItems="start">
          <TypoGraphyKit variant="h6">
            وضعیت اعتبار سنجی: درخواست اصلاح مدارک
          </TypoGraphyKit>
          <TypoGraphyKit variant="body1">کاربر گرامی</TypoGraphyKit>
          <TypoGraphyKit variant="body1">
            مطابق بررسی های انجام شده؛ برخی از مدارک ارسالی شما تایید نشده است.
            لطفا پس از بررسی تصویر و دلیل رد شدن آن؛ نسبت به بارگذاری مجدد تصاویر اقدام نمایید.
          </TypoGraphyKit>
        </StackKit>

        <StackKit justifyContent="space-between" alignItems="start">
          <Grid sx={{ marginBlock: "50px", paddingBlock: "20px" }}>
            <TypoGraphyKit variant="h6">
              تصاویر زیر مورد تایید قرار نگرفته اند:
            </TypoGraphyKit>
          </Grid>

          <StackKit
              direction="row"
              justifyContent="space-around"
              gap={2}
              flexWrap="wrap"
              sx={{ width: "100%" }}
          >
            {IMAGE_CONFIGS.map((config) => {
              if (!shouldShowImage(config)) return null;

              return (
                  <ImageUploadBox
                      key={config.field}
                      src={getImageSrc(config)}
                      title={config.title}
                      description={userInquiry[config.descriptionKey] as string || ""}
                      uploadKey={config.field}
                      onImageChange={handleImageChange}
                  />
              );
            })}
          </StackKit>
        </StackKit>

        <StackKit direction="row" justifyContent="space-between">
          <CustomButton onClick={() => navigate("/")}>
            بازگشت به صفحه اصلی
          </CustomButton>
          <CustomButton onClick={handleSubmit} disabled={isSubmitting}>
            ویرایش
          </CustomButton>
        </StackKit>
      </Grid>
  );
};

export default Correction;